import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { setComposeEmailModal } from '../../../../store/investor/action';
import { COMPOSE_EMAIL_SOURCE } from '../../../../common/NeedInfoV2/NeedInfoV2Constants';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_COMPOSE_EMAIL_OPENED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { getSubdomain, resolveRegisteredCompanyName } from '../../../../utils/utils';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';

/**
 * Whether the Compose Email entry point should be shown. `dataPending.composeEmail` is a SUB-key
 * and visibility keys fail open, so the Data Pending master switch has to hold too — otherwise
 * config places the entry point (the header icon, or the kebab item via secondCTATrigger) for a
 * PNO whose Data Pending is switched off. NeedInfoV2 reads the sub-key alone only because its
 * drawer is already gated by the master key upstream.
 */
export const useComposeEmailVisible = (): boolean => {
    const { dataPendingEnabledVisibility, composeEmailVisibility } = useFeatureVisibility({
        dataPendingEnabledVisibility: VISIBILITY_KEYS.DATA_PENDING.ENABLED,
        composeEmailVisibility: VISIBILITY_KEYS.DATA_PENDING.COMPOSE_EMAIL,
    });
    return dataPendingEnabledVisibility.show && composeEmailVisibility.show;
};

/**
 * Opens the Compose Email modal as a blank canvas (no requirements carried, Data Pending toggle
 * off) for the company in context, and reports it. Shared by the header's compose icon and the
 * three-dot menu item so the two payloads can't drift and the metric can't under-count.
 *
 * Registered name, not the brand: this name is the modal heading AND reaches the Engagement
 * Letter / Bureau & KYC Consent / One Pager drawers as `companyName`
 * (useComposeEmailLogic.openSystemDocDrawer). RECUR-21135 settled that surface on the registered
 * name — same falsy-safe chain as CompanyDashboardParent's companyNameInfo, since orgName can be
 * an empty string.
 */
export const useOpenComposeEmail = (selectedCompany: any) => {
    const dispatch = useDispatch();
    const params = useParams();
    const userData = useAppSelector((state) => state.user.userData);

    return () => {
        dispatch(
            setComposeEmailModal({
                show: true,
                selectedRequirements: [],
                orgName: resolveRegisteredCompanyName(selectedCompany),
                source: COMPOSE_EMAIL_SOURCE.DASHBOARD_HEADER,
            }),
        );

        Mixpanel.track(AICA_COMPOSE_EMAIL_OPENED, {
            userRole: userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
            currentUrl: window.location.href,
            organizationId: params.id,
        });
    };
};

export default useOpenComposeEmail;
